/**
 * Rosie — NEMT Dispatch Platform
 * Copyright (c) 2025-2026 Commonwealth Transport LLC. All rights reserved.
 * PROPRIETARY AND CONFIDENTIAL. Unauthorized use, copying, or distribution is prohibited.
 */
import React, { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import api from '../utils/api';

const user = JSON.parse(localStorage.getItem('nemt_user') || '{}');

const isPaula = user.email === 'paula@commonwealthtransport.com';
const isDispatcher = user.role === 'dispatcher';

const NAV_ITEMS = [
  { to: '/', label: 'Today', exact: true },
  { to: '/schedule', label: 'Schedule' },
  { to: '/drivers', label: 'Drivers' },
  { to: '/requests', label: 'Requests', badge: 'requests' },
  { to: '/coverage', label: 'Coverage' },
  { to: '/checkins', label: 'Check-ins' },
  { to: '/vehicles', label: 'Vehicles' },
  { to: '/payroll', label: 'Payroll' },
  { to: '/billing', label: 'Billing' },
  { to: '/personnel', label: 'Personnel' },
  { to: '/estimate', label: 'Estimate' },
  // '/lgh-chat' removed from nav 2026-07-03 (two doors named LGH chat = wrong-door
  // bookings during the Rooms proving run). Page stays alive at its URL; full
  // retirement is a cutover-day task (BACKLOG B-089).
  { to: '/patients', label: 'Patients' },
  { to: '/training', label: 'Training' },
  { to: '/sheets-compare', label: 'Sheets Compare' },
  // '/ask-rosie' removed from nav 2026-08-13 (Paula): loose-chat booking kept
  // creating bad trips (TRP-2026-2949 booked off an accidental message). Page
  // stays alive at its URL like /lgh-chat (B-089 pattern); SMS texting to
  // Rosie is unaffected. Booking paths are now: fax, LGH room, dashboard forms.
  { to: '/rooms', label: 'Rooms' },
  { to: '/issues', label: 'Issues' },
  ...((isDispatcher || isPaula) ? [{ to: '/my-hours', label: 'My Hours' }] : []),
  ...(isPaula ? [{ to: '/business-rules', label: 'Business Rules' }] : []),
  ...(isPaula ? [{ to: '/console', label: 'Console' }] : []),
];

// /rooms is a mobile bottom-nav primary (one tap from anywhere) during the
// bridge — a dispatcher who has to dig for the room reverts to RDB
// (DISPATCH-SPEED LAW).
const MOBILE_PRIMARY = isPaula
  ? ['/', '/rooms', '/schedule', '/requests', '/console']
  : isDispatcher
  ? ['/', '/rooms', '/schedule', '/requests', '/estimate']
  : ['/', '/rooms', '/schedule', '/requests', '/estimate'];

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [pendingRequests, setPendingRequests] = useState(0);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);

  useEffect(() => {
    const fetchPending = async () => {
      try {
        const res = await api.get('/drivers/requests');
        const count = (res.data.requests || []).filter(r => r.status === 'pending').length;
        setPendingRequests(count);
      } catch (err) {}
    };
    fetchPending();
    const interval = setInterval(fetchPending, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => { setMoreOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!moreOpen) return;
    const handleClick = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [moreOpen]);

  const handleLogout = () => {
    localStorage.removeItem('nemt_token');
    localStorage.removeItem('nemt_user');
    navigate('/login');
  };

  const getBadge = (item) => {
    if (item.badge === 'requests' && pendingRequests > 0) return pendingRequests;
    return null;
  };

  const mobileSecondary = NAV_ITEMS.filter(i => !MOBILE_PRIMARY.includes(i.to));
  const isSecondaryActive = mobileSecondary.some(i => location.pathname === i.to || (!i.exact && location.pathname.startsWith(i.to + '/')));

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f5f5f3' }}>

      <aside style={{
        width: 200, flexShrink: 0, background: 'white',
        borderRight: '0.5px solid #e0e0db',
        display: 'flex', flexDirection: 'column',
        position: 'sticky', top: 0, height: '100vh',
      }} className="sidebar-desktop">
        <div style={{ padding: '20px 16px 12px' }}>
          <div style={{ fontSize: 14, fontWeight: 500 }}>Commonwealth</div>
          <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>NEMT Dispatch</div>
        </div>

        <nav style={{ flex: 1, padding: '8px 0' }}>
          <div style={{ fontSize: 10, color: '#aaa', padding: '8px 16px 4px', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Dispatch</div>
          {NAV_ITEMS.map(item => {
            const badge = getBadge(item);
            return (
              <NavLink key={item.to} to={item.to} end={item.exact}
                style={({ isActive }) => ({
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '8px 16px', fontSize: 13,
                  color: isActive ? '#185FA5' : '#666',
                  borderLeft: isActive ? '2px solid #185FA5' : '2px solid transparent',
                  fontWeight: isActive ? 500 : 400,
                  textDecoration: 'none',
                  background: isActive ? '#f0f6fd' : 'transparent',
                })}>
                {item.label}
                {badge && (
                  <span style={{
                    background: '#E24B4A', color: 'white',
                    borderRadius: '50%', minWidth: 18, height: 18,
                    fontSize: 10, fontWeight: 500,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '0 4px',
                  }}>
                    {badge}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div style={{ padding: '12px 16px', borderTop: '0.5px solid #e0e0db' }}>
          <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>{user.firstName} {user.lastName}</div>
          <button onClick={handleLogout} style={{ fontSize: 12, padding: '4px 10px' }}>Sign out</button>
        </div>
      </aside>

      <main style={{ flex: 1, minWidth: 0 }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 16px' }}>
          <Outlet />
        </div>
      </main>

      <nav style={{
        display: 'none', position: 'fixed', bottom: 0, left: 0, right: 0,
        background: 'white', borderTop: '0.5px solid #e0e0db',
        padding: '6px 0 env(safe-area-inset-bottom, 6px)', zIndex: 100,
      }} className="bottom-nav">
        {NAV_ITEMS.filter(i => MOBILE_PRIMARY.includes(i.to)).map(item => {
          const badge = getBadge(item);
          return (
            <NavLink key={item.to} to={item.to} end={item.exact}
              style={({ isActive }) => ({
                flex: 1, display: 'flex', flexDirection: 'column',
                alignItems: 'center', padding: '4px 0',
                fontSize: 12, color: isActive ? '#185FA5' : '#999',
                textDecoration: 'none', fontWeight: isActive ? 600 : 400,
                position: 'relative',
              })}>
              {item.label}
              {badge && (
                <span style={{
                  position: 'absolute', top: -2, right: '15%',
                  background: '#E24B4A', color: 'white',
                  borderRadius: '50%', minWidth: 16, height: 16,
                  fontSize: 9, fontWeight: 500,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {badge}
                </span>
              )}
            </NavLink>
          );
        })}
        <div ref={moreRef} style={{ flex: 1, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <button onClick={() => setMoreOpen(v => !v)} style={{
            background: 'none', border: 'none', padding: '4px 0', cursor: 'pointer',
            fontSize: 12, color: isSecondaryActive ? '#185FA5' : '#999',
            fontWeight: isSecondaryActive ? 600 : 400,
          }}>
            More
          </button>
          {moreOpen && (
            <div style={{
              position: 'absolute', bottom: '100%', right: 0,
              background: 'white', border: '0.5px solid #e0e0db',
              borderRadius: 10, boxShadow: '0 -4px 20px rgba(0,0,0,0.12)',
              padding: '6px 0', minWidth: 170, marginBottom: 8,
            }}>
              {mobileSecondary.map(item => {
                const badge = getBadge(item);
                const isActive = location.pathname === item.to;
                return (
                  <NavLink key={item.to} to={item.to} end={item.exact}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '10px 16px', fontSize: 14,
                      color: isActive ? '#185FA5' : '#444',
                      fontWeight: isActive ? 500 : 400,
                      textDecoration: 'none',
                      background: isActive ? '#f0f6fd' : 'transparent',
                    }}>
                    {item.label}
                    {badge && (
                      <span style={{
                        background: '#E24B4A', color: 'white',
                        borderRadius: '50%', minWidth: 18, height: 18,
                        fontSize: 10, fontWeight: 500,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        padding: '0 4px',
                      }}>
                        {badge}
                      </span>
                    )}
                  </NavLink>
                );
              })}
              <div style={{ borderTop: '0.5px solid #e0e0db', margin: '4px 0' }} />
              <button onClick={handleLogout} style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '10px 16px', fontSize: 14, color: '#999',
                background: 'none', border: 'none', cursor: 'pointer',
              }}>
                Sign out
              </button>
            </div>
          )}
        </div>
      </nav>

      <style>{`
        @media (max-width: 768px) {
          .sidebar-desktop { display: none !important; }
          .bottom-nav { display: flex !important; }
          main { padding-bottom: 70px; }
        }
      `}</style>
    </div>
  );
}